/**
 * OrgChart — Deckorate "Org Chart" feature.
 *
 * Users build a people list (name, title, reporting line) by hand or by
 * importing a CSV, optionally attach a photo per person and capture a card
 * style from a selected shape, then generate a hierarchy of connected cards —
 * either on the active slide (initials only) or a new slide (with photos).
 *
 * NONE of the layout/insertion logic lives here — the panel wires inputs to the
 * feature's `hooks/useOrgChart` (state), `services/orgCsv` (CSV parse/build) and
 * `services/orgChartBuilder` (Office.js). See `services/orgChartLayout.ts` for
 * the tree maths.
 */

import { useMemo, useRef, useState } from "react";
import type { ChangeEvent } from "react";
import { ModuleHeader } from "../../../../../shared/components/ModuleHeader";
import { getInitials } from "../services/orgChartLayout";
import { buildCsvTemplate } from "../services/orgCsv";
import { isOrgChartSupported } from "../services/orgChartBuilder";
import { useOrgChart } from "../hooks/useOrgChart";
import type { OrgInsertMode, OrgLayout, OrgPerson } from "../../../types";
import styles from "./OrgChart.module.css";

interface OrgChartProps {
  readonly onBack?: () => void;
}

const LAYOUTS: ReadonlyArray<{ value: OrgLayout; label: string }> = [
  { value: "top-down", label: "Top → Down" },
  { value: "left-right", label: "Left → Right" },
];

/** Card colour swatches (hex WITHOUT "#") — AiVA / Williams Lea palette. */
const CARD_COLORS: ReadonlyArray<{ hex: string; label: string }> = [
  { hex: "003865", label: "Deep blue" },
  { hex: "0064B4", label: "WL blue" },
  { hex: "009FDB", label: "Sky" },
  { hex: "1A9E8F", label: "Teal" },
  { hex: "753BBD", label: "Purple" },
  { hex: "2B333B", label: "Slate" },
];

const toHashHex = (h: string): string => `#${h}`;

/** Left indent per reporting level in the people list (px). */
const INDENT_PER_LEVEL_PX = 14;
/** Cycle-guard ceiling when walking the reporting chain. */
const MAX_TREE_DEPTH = 20;
/** Upper bounds for user-picked files, guarding against oversized inputs. */
const MAX_PHOTO_BYTES = 8 * 1024 * 1024; // 8 MB
const MAX_CSV_BYTES = 2 * 1024 * 1024; // 2 MB
/** Suggested filename for the downloadable CSV template. */
const CSV_TEMPLATE_FILENAME = "org-chart-template.csv";

/** Depth of a person in the reporting tree (root = 0), cycle-guarded. Uses a
 * pre-built id→person map so repeated calls stay O(depth), not O(n). */
function depthOf(person: OrgPerson, byId: ReadonlyMap<number, OrgPerson>): number {
  let depth = 0;
  let current: OrgPerson | undefined = person;
  const seen = new Set<number>();
  while (current?.parentId != null && !seen.has(current.id)) {
    seen.add(current.id);
    const parent: OrgPerson | undefined = byId.get(current.parentId);
    if (!parent) break;
    current = parent;
    depth++;
    if (depth > MAX_TREE_DEPTH) break;
  }
  return depth;
}

/** Read a File as a `data:` URL (used for per-person photos). `readAsDataURL`
 * always yields a string result; the guard keeps the type honest. */
function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => reject(reader.error ?? new Error("Could not read file."));
    reader.readAsDataURL(file);
  });
}

export function OrgChart({ onBack }: Readonly<OrgChartProps>) {
  const supported = useMemo(() => isOrgChartSupported(), []);
  const org = useOrgChart();

  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [parentId, setParentId] = useState("");

  const csvInputRef = useRef<HTMLInputElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  // Which person a photo pick is for (set just before opening the file dialog).
  const photoTargetId = useRef<number | null>(null);

  // If the selected manager was removed, the stored id no longer matches any
  // option — fall back to "top-level" so the controlled <select> stays valid.
  const parentValue = org.people.some((p) => String(p.id) === parentId) ? parentId : "";

  // Pre-build id→person and id→depth maps once per change so the list render
  // stays linear (no per-row/per-comparison scans of the people array).
  const peopleById = useMemo(
    () => new Map(org.people.map((p) => [p.id, p])),
    [org.people],
  );
  const depthById = useMemo(() => {
    const map = new Map<number, number>();
    for (const p of org.people) map.set(p.id, depthOf(p, peopleById));
    return map;
  }, [org.people, peopleById]);

  // Sort by tree depth so managers sit above their reports (indentation reads).
  const orderedPeople = useMemo(
    () =>
      [...org.people].sort(
        (a, b) => (depthById.get(a.id) ?? 0) - (depthById.get(b.id) ?? 0),
      ),
    [org.people, depthById],
  );

  const handleAdd = (): void => {
    const parent = parentValue ? Number(parentValue) : null;
    if (org.addPerson(name, title, parent)) {
      setName("");
      setTitle("");
      // Keep the reports-to selection so a manager's reports can be added in a row.
    }
  };

  const handleDownloadTemplate = (): void => {
    const blob = new Blob([buildCsvTemplate()], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = CSV_TEMPLATE_FILENAME;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleCsvSelected = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    if (file.size > MAX_CSV_BYTES) {
      org.reportError("That CSV file is too large (max 2 MB).");
      return;
    }
    try {
      const text = await file.text();
      org.importCsv(text);
    } catch {
      org.reportError("Could not read that CSV file.");
    }
  };

  const openPhotoPicker = (id: number): void => {
    photoTargetId.current = id;
    photoInputRef.current?.click();
  };

  const handlePhotoSelected = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    const id = photoTargetId.current;
    e.target.value = "";
    if (!file || id == null) return;
    if (!file.type.startsWith("image/")) {
      org.reportError("Please choose an image file for the photo.");
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      org.reportError("That image is too large (max 8 MB).");
      return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      org.setPersonPhoto(id, dataUrl);
    } catch {
      org.reportError("Could not read that image.");
    }
  };

  return (
    <div className={styles.panel}>
      <ModuleHeader title="Org Chart" subtitle="Deckorate" onBack={onBack} />
      <div className={styles.body}>
        {!supported && (
          <p className={styles.notice}>
            Org charts need PowerPoint (desktop or web) with PowerPointApi 1.4+.
          </p>
        )}

        <p className={styles.help}>
          Build a hierarchy by hand or import a CSV, then generate a native,
          editable org chart.
        </p>

        {/* Add person — name & title side by side */}
        <div className={styles.row2}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="org-name">
              Name
            </label>
            <input
              id="org-name"
              className={styles.input}
              value={name}
              placeholder="e.g. Jane Smith"
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAdd();
              }}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="org-title">
              Title
            </label>
            <input
              id="org-title"
              className={styles.input}
              value={title}
              placeholder="e.g. VP Engineering"
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAdd();
              }}
            />
          </div>
        </div>

        <label className={styles.label} htmlFor="org-parent">
          Reports To
        </label>
        <select
          id="org-parent"
          className={styles.select}
          value={parentValue}
          onChange={(e) => setParentId(e.target.value)}
        >
          <option value="">(Top-level / No manager)</option>
          {org.people.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
              {p.title ? ` — ${p.title}` : ""}
            </option>
          ))}
        </select>

        <button type="button" className={styles.btnSecondary} onClick={handleAdd}>
          + Add Person
        </button>

        {/* People list header — count + CSV template / import / clear */}
        <div className={styles.peopleHeader}>
          <span className={styles.label} style={{ margin: 0 }}>
            People ({org.people.length})
          </span>
          <span className={styles.headerLinks}>
            <button type="button" className={styles.linkBtn} onClick={handleDownloadTemplate}>
              ⭳ CSV template
            </button>
            <button
              type="button"
              className={styles.linkBtn}
              onClick={() => csvInputRef.current?.click()}
            >
              ⭱ Import CSV
            </button>
            {org.people.length > 0 && (
              <button
                type="button"
                className={`${styles.linkBtn} ${styles.linkDanger}`}
                onClick={org.clearPeople}
              >
                Clear all
              </button>
            )}
          </span>
        </div>

        <input
          ref={csvInputRef}
          type="file"
          accept=".csv,text/csv"
          className={styles.hiddenInput}
          onChange={handleCsvSelected}
        />
        <input
          ref={photoInputRef}
          type="file"
          accept="image/*"
          className={styles.hiddenInput}
          onChange={handlePhotoSelected}
        />

        {org.people.length === 0 ? (
          <p className={styles.emptyMsg}>No people added yet.</p>
        ) : (
          <div className={styles.peopleList}>
            {orderedPeople.map((p) => {
              const manager = p.parentId != null ? peopleById.get(p.parentId) : undefined;
              const depth = depthById.get(p.id) ?? 0;
              return (
                <div
                  key={p.id}
                  className={styles.personRow}
                  style={{ marginLeft: depth * INDENT_PER_LEVEL_PX }}
                >
                  <span
                    className={styles.personAvatar}
                    style={org.showPhotoHead ? { background: toHashHex(org.cardColor) } : undefined}
                    aria-hidden="true"
                  >
                    {p.photoDataUrl ? (
                      <img className={styles.avatarImg} src={p.photoDataUrl} alt="" />
                    ) : (
                      getInitials(p.name)
                    )}
                  </span>
                  <span className={styles.personInfo}>
                    <span className={styles.personName}>{p.name}</span>
                    <span className={styles.personMeta}>
                      {p.title || "—"}
                      {manager ? ` · ↳ ${manager.name}` : ""}
                    </span>
                  </span>
                  {org.showPhotoHead && (
                    <button
                      type="button"
                      className={`${styles.iconBtn} ${p.photoDataUrl ? styles.iconBtnActive : ""}`}
                      aria-label={p.photoDataUrl ? `Replace photo for ${p.name}` : `Add photo for ${p.name}`}
                      title={p.photoDataUrl ? "Photo added — replace" : "Add photo"}
                      onClick={() => openPhotoPicker(p.id)}
                    >
                      📷
                    </button>
                  )}
                  <button
                    type="button"
                    className={styles.removeBtn}
                    aria-label={`Remove ${p.name}`}
                    onClick={() => org.removePerson(p.id)}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Layout */}
        <fieldset className={styles.fieldset}>
          <legend className={styles.label}>Layout</legend>
          <div className={styles.seg}>
            {LAYOUTS.map((l) => (
              <button
                key={l.value}
                type="button"
                className={`${styles.segBtn} ${org.layout === l.value ? styles.segBtnActive : ""}`}
                aria-pressed={org.layout === l.value}
                onClick={() => org.setLayout(l.value)}
              >
                {l.label}
              </button>
            ))}
          </div>
        </fieldset>

        {/* Card colour */}
        <fieldset className={styles.fieldset}>
          <legend className={styles.label}>Card Colour</legend>
          <div className={styles.chips}>
            {CARD_COLORS.map((c) => (
              <button
                key={c.hex}
                type="button"
                className={styles.swatch}
                title={c.label}
                aria-label={c.label}
                aria-pressed={org.cardColor === c.hex}
                onClick={() => org.setCardColor(c.hex)}
                style={{
                  background: toHashHex(c.hex),
                  outline:
                    org.cardColor === c.hex ? "2px solid var(--wl-blue)" : undefined,
                  outlineOffset: 2,
                }}
              />
            ))}
            <input
              type="color"
              className={styles.colorInput}
              aria-label="Custom card colour"
              value={toHashHex(org.cardColor)}
              onChange={(e) => org.setCardColor(e.target.value.replace(/^#/, "").toUpperCase())}
            />
          </div>
        </fieldset>

        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={org.showPhotoHead}
            onChange={(e) => org.setShowPhotoHead(e.target.checked)}
          />{" "}
          <span>
            <span>Include photo heads</span>
            <small className={styles.checkboxHint}>
              Initials by default; use the camera on each person to add a photo.
            </small>
          </span>
        </label>

        {/* Card template */}
        <fieldset className={styles.fieldset}>
          <legend className={styles.label}>Card Template</legend>
          <div className={styles.templateRow}>
            <span className={org.template ? styles.templateSet : styles.templateNone}>
              {org.template ? "Template: selected shape" : "Default card style"}
            </span>
            <button
              type="button"
              className={styles.linkBtn}
              onClick={() => {
                org.captureTemplate().catch(() => undefined);
              }}
              disabled={org.capturingTemplate}
            >
              {org.capturingTemplate ? "Reading…" : "Use selected shape"}
            </button>
            {org.template && (
              <button
                type="button"
                className={styles.removeBtn}
                aria-label="Clear card template"
                onClick={org.clearTemplate}
              >
                ×
              </button>
            )}
          </div>
        </fieldset>

        {/* Photo insert destination — only relevant once photos exist */}
        {org.showPhotoHead && org.hasPhotos && (
          <div className={styles.notice}>
            <b>Photos added</b> — choose where to insert (photos are included either way):
            <div className={styles.seg} style={{ marginTop: 6 }}>
              {(
                [
                  { value: "current", label: "Active slide" },
                  { value: "newSlide", label: "New slide" },
                ] as ReadonlyArray<{ value: OrgInsertMode; label: string }>
              ).map((m) => (
                <button
                  key={m.value}
                  type="button"
                  className={`${styles.segBtn} ${org.insertMode === m.value ? styles.segBtnActive : ""}`}
                  aria-pressed={org.insertMode === m.value}
                  onClick={() => org.setInsertMode(m.value)}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer stays pinned so Create is always reachable as the list grows. */}
      <div className={styles.footer}>
        {org.status && <p className={styles.notice}>{org.status}</p>}
        {org.error && <p className={styles.error}>{org.error}</p>}
        <button
          type="button"
          className={styles.btnPrimary}
          onClick={() => {
            org.generate().catch(() => undefined);
          }}
          disabled={org.busy || org.people.length === 0}
        >
          {org.busy ? "Generating…" : "Create Org Chart"}
        </button>
      </div>
    </div>
  );
}
